import { Flex, Layout, MenuProps, Typography, notification } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { colorBgButtonSubtleHovered, colorBgSuccessHovered, colorText } from 'src/app/themes/primaryThemeDefault'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import { EyeIcon, PenIcon, UserIcon } from 'src/assets/icons'
import ViewUsersModal from './view-user-by-role/VIewUsersModal'
import { getFilteredUserRoleList } from 'src/app/shared/services/UserRoleService'
import CommonTag from 'src/app/shared/components/common/tag'
import { useUserSetting } from 'src/app/shared/hooks/useUserSetting'
import { TABLE_NAME } from 'src/app/shared/constants/tableName.constants'

import RichTextField from 'src/app/shared/components/common/rich-text-field'
import './index.scss'
import { useRolePermission } from 'src/app/shared/hooks/useRolePermission'
import { EPermissions, ESubModules } from 'src/app/shared/models/roleAndPermission.model'
import NewCommonTable from 'src/app/shared/components/common/new-table'
import { TableColumnDefs } from 'src/app/shared/components/common/new-table/table.model'
import { useSelector } from 'react-redux'
import { getUserProfile } from 'src/app/store/auth'
import { getUserListByRole } from 'src/app/shared/services/ClientSetupService'

const builtInRole = ['Bank Admin', 'Bank User', 'Bank User (View Only)', 'Client Admin User', 'Client User (View Only)']

export const isViewPermissionOnly = (yourRole: string, role2Check: string, isSystemAdmin: boolean) => {
  if (role2Check === 'Super Admin') return true
  if (yourRole === 'Super Admin' || isSystemAdmin) return false
  if (yourRole === 'Super Admin' && builtInRole.includes(role2Check)) {
    return false
  }
  if (!builtInRole.includes(role2Check)) {
    return false
  }
  return true
}

function UserRoles() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { isGettingSetting, isUpdatingSetting } = useUserSetting()
  const [api, contextHolder] = notification.useNotification()
  const userProfile = useSelector(getUserProfile)

  const { userPermissions } = useRolePermission()
  const isSystemAdmin: boolean = userPermissions[ESubModules.SPECIAL_PERMISSION]?.includes(EPermissions.FULL) || false
  const [isGettingList, setIsGettingList] = useState<boolean>(false)
  const [userRoleList, setUserRoleList] = useState<any>({
    items: [],
    total: 0,
    pageIndex: 0,
    pageSize: 10,
  })

  const { state } = location
  useEffect(() => {
    if (state?.userRole?.message) {
      api.success({
        message: <RichTextField richText={state?.userRole?.message} />,
        closeIcon: null,
      })
    }
  }, [state?.userRole])

  const [isViewUsersModalOpen, setIsViewUsersModalOpen] = useState<boolean>(false)
  const [userRoleSelected, setUserRoleSelected] = useState('')

  const [listUsers, setListUsers] = useState<any[]>()
  const getDataUserRoleList = useCallback(async (userId: string) => {
    try {
      const data = await getUserListByRole(userId)
      setListUsers(data.data)
    } catch (error) {
      console.log('ERROR: ', error)
    }
  }, [])

  const handleViewListAccount = (rowData: any) => {
    getDataUserRoleList(rowData.userRoleId)
    setUserRoleSelected(rowData.userRoleName)
    setIsViewUsersModalOpen(true)
  }

  const getItems = useCallback(
    (rowData: any): MenuProps['items'] => {
      return [
        userPermissions[ESubModules.USER_ROLE]?.includes(EPermissions.VIEW_ITEMS)
          ? {
              key: '1',
              label: <Typography.Text style={{ fontSize: '14px' }}>{t('UserRoles:viewUsers')}</Typography.Text>,
              icon: <UserIcon />,
              onClick: () => {
                handleViewListAccount(rowData)
              },
            }
          : null,
        userPermissions[ESubModules.USER_ROLE]?.includes(EPermissions.VIEW_ITEMS)
          ? {
              key: '2',
              label: <Typography.Text style={{ fontSize: '14px' }}>{t('UserRoles:viewRecord')}</Typography.Text>,
              icon: <EyeIcon />,
              onClick: () => {
                if (rowData?.userRoleId) navigate(`${menuUrls.viewUserRole}/${rowData?.userRoleId}`)
              },
            }
          : null,
        userPermissions[ESubModules.USER_ROLE]?.includes(EPermissions.ADD_EDIT_ITEMS) &&
        !isViewPermissionOnly(userProfile?.defaultRole ?? '', rowData?.roleName, isSystemAdmin)
          ? {
              key: '3',
              label: (
                <Typography.Text style={{ fontSize: '14px', color: colorText }}>
                  {t('UserRoles:editRecord')}
                </Typography.Text>
              ),
              icon: <PenIcon />,
              onClick: () => {
                if (rowData?.userRoleId) navigate(`${menuUrls.editUserRole}/${rowData?.userRoleId}`)
              },
            }
          : null,
      ]
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, navigate],
  )

  const generateCustomColumnDefs = useCallback(
    (columnDefs: Array<TableColumnDefs>): Array<TableColumnDefs> => {
      return columnDefs.map((item: TableColumnDefs, index: any) => {
        if (item.colId === 'navigationOptions') {
          item.cellRenderer = (props: any) => {
            return (
              <RichTextField
                richText={
                  props?.data?.navigationOption instanceof Array
                    ? props?.data?.navigationOptions?.join(', ')
                    : props?.data?.navigationOptions ?? ''
                }
              />
            )
          }
        }

        if (item.colId === 'permissions') {
          item.cellRenderer = (props: any) => {
            return (
              <RichTextField
                richText={
                  props?.data?.permissions instanceof Array
                    ? props?.data?.permissions?.join('<br/>')
                    : props?.data?.permissions.replaceAll('\n', '<br/>') ?? ''
                }
              />
            )
          }
        }

        if (item.colId === 'emails') {
          item.cellRenderer = (props: any) => {
            return (
              <RichTextField
                richText={
                  props?.data?.emails instanceof Array
                    ? props?.data?.emails?.join('<br/>')
                    : props?.data?.emails.replaceAll('\n', '<br/>') ?? ''
                }
              />
            )
          }
        }

        if (item.colId === 'viewPermissionOnly') {
          return {
            ...item,
            width: 150,
            cellRenderer: (props: any) => {
              return (
                <Flex style={{ display: 'flex', justifyContent: 'left' }}>
                  {props?.data?.viewPermissionOnly === 'Yes' ? (
                    <CommonTag color='success' style={{ background: colorBgSuccessHovered }} displayLeftDot>
                      {t('common:yes')}
                    </CommonTag>
                  ) : (
                    <CommonTag color='default' style={{ background: colorBgButtonSubtleHovered }} displayLeftDot>
                      {t('common:no')}
                    </CommonTag>
                  )}
                </Flex>
              )
            },
          } as any
        }
        return item
      })
    },
    [t],
  )

  const handleAddRecord = () => {
    navigate(menuUrls.addUserRole)
  }

  const getUserRoleList = useCallback(async (payload: any) => {
    try {
      setIsGettingList(true)
      const res = await getFilteredUserRoleList(payload)
      if (res?.data) {
        setUserRoleList(res?.data)
      }
    } catch (error) {
      console.log('ERROR: ', error)
    } finally {
      setIsGettingList(false)
    }
  }, [])

  const handleSearch = useCallback(
    async (value: Partial<any>) => {
      await getUserRoleList(value)
    },
    [getUserRoleList],
  )

  return (
    <Flex className='user-role-page'>
      <Layout.Content style={{ padding: 24 }}>
        {contextHolder}
        <NewCommonTable
          rowData={userRoleList?.items}
          total={userRoleList?.total}
          displayAddBtn={userPermissions[ESubModules.USER_ROLE]?.includes(EPermissions.ADD_EDIT_ITEMS)}
          displayUploadButton={false}
          displayDownloadBtn={userPermissions[ESubModules.USER_ROLE]?.includes(EPermissions.VIEW_ITEMS)}
          displayActionColumn
          isGettingList={isGettingList}
          isUpdatingSetting={isUpdatingSetting}
          isGettingSetting={isGettingSetting}
          pageName={TABLE_NAME.USER_ROLE}
          actionDropdownItems={getItems}
          handleSearch={handleSearch}
          handleDownload={async (payload) => await getFilteredUserRoleList(payload)}
          handleAddRecord={handleAddRecord}
          tableSettingName={TABLE_NAME.USER_ROLE}
          translationNamespace='UserRoles:viewUserRole'
          generateCustomColumnDefs={generateCustomColumnDefs}
        />
        <ViewUsersModal
          isOpen={isViewUsersModalOpen}
          onClose={() => setIsViewUsersModalOpen(false)}
          userRole={userRoleSelected}
          listUser={listUsers}
        />
      </Layout.Content>
    </Flex>
  )
}

export default UserRoles
